import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { PricingTier } from '../types';

const PricingPreviewSection = () => {
  const pricingTiers: PricingTier[] = [
    {
      id: "1",
      name: "Starter",
      price: "₹88,000",
      description: "Perfect for small businesses starting their automation journey",
      features: [
        "1 Automation Workflow",
        "Up to 1,000 operations/month",
        "Email Support",
        "Basic Analytics",
        "30-day Money-back Guarantee"
      ],
      cta: "Get Started"
    },
    {
      id: "2",
      name: "Growth",
      price: "₹2,20,000",
      description: "Ideal for growing businesses scaling their operations",
      features: [
        "5 Automation Workflows",
        "Up to 10,000 operations/month",
        "Priority Support",
        "Advanced Analytics",
        "Custom Integrations",
        "Quarterly Strategy Calls"
      ],
      popular: true,
      cta: "Start Growing"
    },
    {
      id: "3",
      name: "Enterprise",
      price: "Custom",
      description: "Tailored solutions for large-scale automation needs",
      features: [
        "Unlimited Workflows",
        "Unlimited Operations",
        "Dedicated Account Manager",
        "24/7 Premium Support",
        "Custom Development",
        "SLA Guarantee"
      ],
      cta: "Contact Sales"
    }
  ];

  return (
    <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-16 lg:py-24">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-success/10 rounded-full">
            <Icon name="DollarSign" size={16} className="text-success" />
            <span className="text-sm font-medium text-success">Transparent Pricing</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary">
            Investment That Pays for Itself
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Choose a plan that fits your needs. All plans include our 30-day money-back guarantee.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
          {pricingTiers.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-card rounded-2xl border p-8 hover:shadow-lg transition-all duration-300 ${
                tier.popular
                  ? 'border-primary shadow-lg scale-105'
                  : 'border-border hover:border-primary'
              }`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="px-4 py-1 bg-primary text-white text-sm font-medium rounded-full">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-text-primary mb-2">{tier.name}</h3>
                <div className="text-4xl font-bold text-primary mb-2">{tier.price}</div>
                {tier.price !== "Custom" && (
                  <div className="text-sm text-text-secondary">per month</div>
                )}
                <p className="text-sm text-text-secondary mt-4">{tier.description}</p>
              </div>

              <div className="space-y-3 mb-8">
                {tier.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Icon name="Check" size={20} className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">{feature}</span>
                  </div>
                ))}
              </div>

              <Link to="/pricing">
                <Button
                  variant={tier.popular ? "default" : "outline"}
                  size="lg"
                  fullWidth
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  {tier.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link to="/pricing">
            <Button variant="ghost" size="lg" iconName="Calculator" iconPosition="left">
              View Detailed Pricing & ROI Calculator
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PricingPreviewSection;